// core
import React from "react";
import { useFormikContext } from "formik";
import Toggle from "../../../../common/components/Forms/Toggle/Toggle";
import Input from "../../../../common/components/Forms/Input/Input";
import { IClientSettingsBoxProduct } from "../ClientSettingsBox/ClientSettingsBox";
import ToolTip from "../../../../common/components/Tooltips/Tooltip";
import TraumaType from "../../../../common/interfaces/enums/TraumaType";
import ProductType from "../../../../common/interfaces/enums/ProductType";
import { ITraumaQuoteClient } from "../../../interfaces/form/trauma/ITraumaQuoteClient";
import { IQuoteSettings } from "../../../interfaces/form/IQuoteSettings";
import useSettingsValidation from "../../../hooks/useSettingsValidation";

type ClientSettingsBoxTraumaProps = IClientSettingsBoxProduct;

const ClientSettingsBoxTrauma: React.FC<ClientSettingsBoxTraumaProps> = (props) => {
    const { client, name } = props;
    const {
        handleBlur,
        validateField,
        setFieldTouched,
        setFieldValue
    } = useFormikContext<IQuoteSettings>();

    const { getFormikError } = useSettingsValidation();

    const typeOfTraumaOptions = [
        { id: TraumaType.Accelerated, label: "Accelerated" },
        { id: TraumaType.Standalone, label: "Standalone" }
    ];

    if (client.isChild) {
        return <p>Children are not eligible for Trauma insurance</p>;
    }

    const setClient = <T extends keyof ITraumaQuoteClient>(
        key: T,
        value: ITraumaQuoteClient[T]
    ): void => {
        props.setClient({
            ...client,
            [ProductType.Trauma]: {
                ...client[ProductType.Trauma],
                [key]: value
            }
        });
    };

    return (
        <React.Fragment>
            <Input
                name={`${name}.${ProductType.Trauma}.coverAmount`}
                label={`Cover amount`}
                type={`number`}
                hasCurrency
                value={client[ProductType.Trauma]?.coverAmount}
                onBlur={handleBlur}
                onChange={(e) =>
                    setFieldValue(`${name}.${ProductType.Trauma}.coverAmount`, e)
                }
                hasError={getFormikError(`${name}.${ProductType.Trauma}.coverAmount`)}>
                <ToolTip
                    className="gap-pre-s"
                    tooltipContent={
                        "Common uses for Trauma Cover are help with medical bills, take time off work to recover, and to pay off a mortgage or debt."
                    }>
                    How much to choose?
                </ToolTip>
            </Input>
            <Toggle
                name={`${name}.${ProductType.Trauma}.traumaType`}
                label={`Cover type`}
                options={typeOfTraumaOptions.map((o) => {
                    return {
                        ...o,
                        selected: o.id === client[ProductType.Trauma]?.traumaType
                    };
                })}
                onChange={(e) => {
                    setClient("traumaType", e as TraumaType);
                    validateField(`${name}.${ProductType.Trauma}`);
                    setFieldTouched(`${name}.${ProductType.Trauma}.traumaType`);
                }}
                hasError={
                    getFormikError(`${name}.${ProductType.Trauma}.traumaType`) ||
                    getFormikError(`${name}.${ProductType.Trauma}`)
                }
                className="gap-ml">
                <ToolTip
                    className="gap-pre-s"
                    tooltipContent={
                        '"Accelerated" is an early payment of your Life Cover (if you have $500,000 Life Cover and $100,000 Trauma Cover and make a Trauma claim, your Life Cover will reduce to $400,000). "Standalone" does not affect your Life Cover.'
                    }>
                    What&apos;s the difference?
                </ToolTip>
            </Toggle>
        </React.Fragment>
    );
};

export default ClientSettingsBoxTrauma;
